import { AppTable, LoaderBar, PageHeader } from 'components'
import { Button, Card, Form } from 'semantic-ui-react'
import { useMemo, useState, useEffect } from 'react'
import {
    GetUserList,
    GetCompanyTemplateList,
    GetCustomerList,
    useCustomerGroups,
    useNotification,
} from 'hooks'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { FormDateField, FormDropdownField, SubmitCancelButtons } from 'elements'
import { AuthService, DatePickerTypes, DateUtility } from 'utility'
import { useNavigate } from 'react-router-dom'
import { GetCompletedAudits } from 'hooks/invoice'
import { DesktopMode, MobileMode } from 'layout'

const ReportSchema = yup.object().shape({
    StartDate: yup
        .string()
        .required('Start date required')
        .typeError('Start date required'),
    EndDate: yup
        .string()
        .required('End date required')
        .typeError('End date required'),
    Status: yup.string(),
    UserId: yup.array().min(0).nullable(),
    CustomerIds: yup.array().min(0).nullable(),
    TemplateId: yup.string(),
    GroupId: yup.string().nullable(),
})

const AuditInvoiceCard = ({ item, selectedAudits = [], handleSelectAudit, onReopen }) => {
    const id = item?.TaskId || item?.AuditId
    const isSelected = selectedAudits.includes(id)
    return (
        <Card fluid className="mb-3">
            <Card.Content>
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <Card.Header style={{ fontWeight: 600, color: '#0f172a' }}>
                            {item?.AuditName}
                        </Card.Header>
                        <Card.Meta className="mt-1">
                            <div>Customer: {item?.CustomerName}</div>
                            <div>Created By: {item?.EmailAddress || item?.CreatedByName}</div>
                            <div>
                                Completed:{' '}
                                {item?.CompletedOn &&
                                    new Date(item?.CompletedOn).toLocaleString()}
                            </div>
                            {!!item?.ReportLink && (
                                <div className="mt-1">
                                    <a
                                        href={item?.ReportLink}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Download Report
                                    </a>
                                </div>
                            )}
                        </Card.Meta>
                    </div>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectAudit(id)}
                        aria-label={`Select ${item?.AuditName || 'audit'}`}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                </div>
                <div className="mt-3 d-flex justify-content-end">
                    <Button
                        color="blue"
                        size="small"
                        disabled={selectedAudits.length > 0}
                        onClick={() => onReopen(item)}
                    >
                        Generate Invoice
                    </Button>
                </div>
            </Card.Content>
        </Card>
    )
}

export const InvoiceListScreen = () => {
    const {
        data,
        loading,
        filterChanged,
        // filter,
        // totalCount,
        setData,
        pagination,
        pageChanged,
    } = GetCompletedAudits()

    const { options: userOptions } = GetUserList()
    const { options: templateOptions } = GetCompanyTemplateList()
    const { options: customerList } = GetCustomerList()

    // Company and customer groups
    const companyId = AuthService.getUser()?.Companies?.[0]?.CompanyId
    const customerGroups = useCustomerGroups(companyId)
    const groupOptions = useMemo(
        () =>
            (customerGroups || []).map(g => ({
                value: g.CustomerGroupId,
                text: g.CustomerGroupName,
            })),
        [customerGroups],
    )

    const naviagate = useNavigate()
    const { warningToast } = useNotification()

    // Multi-select state
    const [selectedAudits, setSelectedAudits] = useState([])
    const [isGenerating] = useState(false)

    const {
        control,
        handleSubmit,
        reset,
        watch,
        getValues,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(ReportSchema),
        defaultValues: {
            StartDate: new Date(),
            EndDate: new Date(),
            Target: 95,
            GroupId: '',
        },
    })
    const StartDate = watch('StartDate')

    const submit = formData => {
        if (!companyId) {
            return
        }

        const payload = {
            CompanyId: Number(companyId),
            StartDate: DateUtility.dateToLocalISO(
                DateUtility.startDay(new Date(formData.StartDate)),
            ),
            EndDate: DateUtility.dateToLocalISO(
                DateUtility.endDay(new Date(formData.EndDate)),
            ),
            TemplateId: formData.TemplateId || '',
            GroupId:
                formData.GroupId === undefined || formData.GroupId === ''
                    ? 0
                    : Number(formData.GroupId),
            UserId: formData.UserId?.length
                ? formData.UserId?.toString()?.replaceAll(',', ';')
                : '',
            CustomerIds: formData.CustomerIds?.length
                ? formData.CustomerIds?.toString()?.replaceAll(',', ';')
                : '',
            PageNumber: 1,
            PageSize: 50,
        }

        filterChanged(payload)
    }

    const resetForm = async () => {
        reset({
            StartDate: new Date(),
            EndDate: new Date(),
            UserId: undefined,
            Target: 95,
            GroupId: '',
        })
        setData([])
    }

    // Single-audit flow: open editor with unified SelectedAudits
    const onReopen = async item => {
        const SelectedAudits = [
            {
                AuditId: item?.TaskId,
                TemplateId: item?.TemplateId,
                CustomerId: item?.CustomerId,
                CustomerName: item?.CustomerName,
                AuditName: item?.AuditName,
            },
        ]
        naviagate(`/app/invoice/editor/new`, {
            state: {
                mode: 'create',
                TaskId: item?.TaskId,
                CustomerId: item?.CustomerId,
                CustomerName: item?.CustomerName,
                TemplateId: item?.TemplateId,
                AuditName: item?.AuditName,
                CreatedByName: item?.CreatedByName,
                SelectedAudits,
                EmailAddress: item?.EmailAddress,
            },
        })
    }

    const handleSelectAudit = auditId => {
        setSelectedAudits(prev => {
            if (prev.includes(auditId)) {
                return prev.filter(id => id !== auditId)
            }
            return [...prev, auditId]
        })
    }

    const handleSelectAll = () => {
        if (!Array.isArray(data) || data.length === 0) {
            setSelectedAudits([])
            return
        }
        if (selectedAudits.length === data.length) {
            setSelectedAudits([])
        } else {
            setSelectedAudits(data.map(item => item.AuditId || item.TaskId))
        }
    }

    const handleClearSelection = () => {
        setSelectedAudits([])
    }

    // Multi-select flow: open editor with SelectedAudits
    const createInvoiceFromSelection = () => {
        try {
            const selectedItems = (data || []).filter(d =>
                selectedAudits.includes(d.AuditId || d.TaskId),
            )
            if (!selectedItems.length) return

            const uniqueCustomers = Array.from(
                new Set(selectedItems.map(x => x.CustomerId)),
            )
            if (uniqueCustomers.length > 1) {
                warningToast('Please select audits for the same customer')
                return
            }

            const first = selectedItems[0]
            const SelectedAudits = selectedItems.map(x => ({
                AuditId: x.TaskId || x.AuditId,
                TemplateId: x.TemplateId,
                CustomerId: x.CustomerId,
                CustomerName: x.CustomerName,
                AuditName: x.AuditName,
                EmailAddress: x.EmailAddress,
            }))

            naviagate(`/app/invoice/editor/new`, {
                state: {
                    mode: 'create',
                    CustomerId: first?.CustomerId,
                    CustomerName: first?.CustomerName,
                    TemplateId: first?.TemplateId,
                    SelectedAudits,
                    EmailAddress: first?.EmailAddress,
                },
            })
        } catch (e) {
            // noop
        }
    }

    // Trigger initial load only after companyId is available
    useEffect(() => {
        if (!companyId) return
        const defaults = getValues()
        submit(defaults)
    }, [companyId])

    const columns = useMemo(
        () => [
            {
                title: (
                    <input
                        type="checkbox"
                        checked={
                            selectedAudits.length > 0 &&
                            selectedAudits.length === (data?.length || 0)
                        }
                        onChange={handleSelectAll}
                        aria-label="Select all audits"
                    />
                ),
                key: 'checkbox',
                width: '50px',
                render: item => (
                    <input
                        type="checkbox"
                        checked={selectedAudits.includes(item.TaskId || item.AuditId)}
                        onChange={() =>
                            handleSelectAudit(item.TaskId || item.AuditId)
                        }
                        aria-label={`Select ${item?.AuditName || 'audit'}`}
                    />
                ),
            },
            {
                title: 'Audit Name',
                key: 'AuditName',
                render: item => (
                    <div>
                        <span>{item?.AuditName}</span>
                        {!!item?.ReportLink && (
                            <div>
                                <a
                                    href={item?.ReportLink}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Download
                                </a>
                            </div>
                        )}
                    </div>
                ),
            },
            {
                title: 'Customer Name',
                key: 'CustomerName',
            },
            {
                title: 'Completed On',
                render: item => (
                    <span>
                        {item?.CompletedOn &&
                            new Date(item?.CompletedOn).toLocaleString()}
                    </span>
                ),
            },
            {
                title: 'Created By',
                key: 'EmailAddress',
            },
            {
                title: 'Action',
                render: item => (
                    <Button
                        color="blue"
                        disabled={Boolean(selectedAudits.length)}
                        onClick={() => onReopen(item)}
                    >
                        Generate Invoice
                    </Button>
                ),
            },
        ],
        [selectedAudits, data, handleSelectAll, handleSelectAudit, onReopen],
    )

    return (
        <>
            {loading && <LoaderBar content="Loading Audits..." />}
            <div className="">
                <PageHeader header="Invoice Report" />
                <Form noValidate>
                    <Form.Group widths="equal">
                        <FormDateField
                            name="StartDate"
                            control={control}
                            label="Start Date"
                            errors={errors.StartDate}
                            type={DatePickerTypes.Single}
                        />
                        <FormDateField
                            name="EndDate"
                            control={control}
                            label="End Date"
                            disabledBefore={StartDate}
                            errors={errors.EndDate}
                            type={DatePickerTypes.Single}
                        />
                        <FormDropdownField
                            control={control}
                            placeholder="Select User"
                            name="UserId"
                            label="User"
                            errors={errors.UserId}
                            options={userOptions}
                            defaultValue={[]}
                            multiple
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            name="GroupId"
                            label="Group"
                            placeholder="Select Group"
                            errors={errors.GroupId}
                            options={groupOptions}
                            defaultValue=""
                        />
                        <FormDropdownField
                            control={control}
                            name="CustomerIds"
                            label="Customers"
                            errors={errors.CustomerIds}
                            options={customerList}
                            defaultValue={[]}
                            placeholder="Select Customers"
                            multiple
                        />
                        <FormDropdownField
                            control={control}
                            name="TemplateId"
                            label="Template"
                            placeholder="Select Template"
                            errors={errors.TemplateId}
                            options={templateOptions}
                            defaultValue=""
                        />
                    </Form.Group>

                    {/* Desktop: show Clear + Generate + Submit/Cancel in one line */}
                    <DesktopMode>
                        <div
                            className="d-flex justify-content-end gap-1 align-items-center flex-nowrap"
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {selectedAudits.length > 0 && (
                                <Button basic onClick={handleClearSelection}>
                                    Clear Selection
                                </Button>
                            )}
                            <Button
                                primary
                                onClick={createInvoiceFromSelection}
                                disabled={
                                    selectedAudits.length === 0 || isGenerating
                                }
                                loading={isGenerating}
                            >
                                Generate Invoice
                            </Button>
                            <div className="d-inline-flex ms-2">
                                <SubmitCancelButtons
                                    onSubmit={handleSubmit(submit)}
                                    onCancel={resetForm}
                                    className=""
                                />
                            </div>
                        </div>
                    </DesktopMode>

                    {/* Mobile: Submit/Cancel in its own row */}
                    <MobileMode>
                        <div className="pt-1">
                            <SubmitCancelButtons
                                onSubmit={handleSubmit(submit)}
                                onCancel={resetForm}
                                className=""
                            />
                        </div>
                    </MobileMode>
                </Form>
            </div>

            {/* Desktop and Mobile Views using AppTable */}
            <div className="my-3">
                <MobileMode>
                    <div className="d-flex align-items-center mb-3 justify-content-between flex-wrap gap-2">
                        <div className="form-check me-2">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={
                                    selectedAudits.length > 0 &&
                                    selectedAudits.length === (data?.length || 0)
                                }
                                onChange={handleSelectAll}
                                id="selectAllMobile"
                            />
                            <label
                                className="form-check-label ms-1"
                                htmlFor="selectAllMobile"
                            >
                                Select All
                            </label>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                color="blue"
                                size="small"
                                disabled={!selectedAudits.length}
                                onClick={createInvoiceFromSelection}
                            >
                                Generate Invoice ({selectedAudits.length})
                            </Button>
                            {selectedAudits.length > 0 && (
                                <Button
                                    basic
                                    size="small"
                                    onClick={handleClearSelection}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </div>
                </MobileMode>

                <AppTable
                    columns={columns}
                    data={data || []}
                    filter={{
                        PageNumber: pagination.currentPage,
                        PageSize: pagination.perPage,
                    }}
                    pageChanged={pageChanged}
                    totalCount={pagination.total}
                    emptyMessage="No Completed Audits Found"
                    mobileCard={
                        <AuditInvoiceCard
                            selectedAudits={selectedAudits}
                            handleSelectAudit={handleSelectAudit}
                            onReopen={onReopen}
                        />
                    }
                />
            </div>
        </>
    )
}
